import React from 'react';
import useSWR from 'swr';
import { mutate as globalMutate } from 'swr';
import api from '../../../services/api';
import { fetcher } from '../../../services/fetcher';
import { moduleConfig } from './config';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../../services/paginatedList';

export interface Vessel {
  id: number;
  vesselName: string;
  vesselAbrv?: string | null;
  vesselTypeId?: number | null;
  vesselType?: { id: number; code?: string | null; name: string; isActive?: boolean } | null;
  imoNumber?: string | null;
  callSign?: string | null;
  flagId?: number | null;
  flag?: { id: number; name: string; isoCode2?: string | null; isoCode3?: string | null; isActive?: boolean } | null;
  ownerId?: number | null;
  owner?: { id: number; code?: string | null; name: string; isActive?: boolean } | null;
  managerId?: number | null;
  manager?: { id: number; code: string; name: string; isActive?: boolean } | null;
  engId?: number | null;
  engineType?: { id: number; code?: string | null; name: string; isActive?: boolean } | null;
  mainEngType?: string | null;
  bhp?: string | null;
  auxEng?: string | null;
  craneTypes?: string | null;
  ecdisType?: number | null;
  ecdisTypeRef?: { id: number; code?: string | null; name: string; isActive?: boolean } | null;
  ecdisModel?: string | null;
  officialNo?: string | null;
  grt?: number | null;
  nrt?: number | null;
  dwt?: number | null;
  pnumber?: string | null;
  telex?: string | null;
  email?: string | null;
  notes?: string | null;
  safeEquip: boolean;
  safeManning?: number | null;
  isActive: boolean;
  isVessel: boolean;
  stampStorageKey?: string | null;
  stampOriginalFilename?: string | null;
  stampMimeType?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const useVessels = (
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) => {
  const q = buildListQuery(searchParams, page, limit, options);
  const { data, error, isLoading, mutate } = useSWR<PaginatedApiResponse<Vessel> | Vessel[]>(
    `${moduleConfig.module.api.list}${q}`,
    fetcher,
    { revalidateOnFocus: true },
  );
  const normalized = normalizePaginatedListResponse<Vessel>(data, page, limit);
  return {
    data: normalized.data,
    clients: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useVessel = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<Vessel>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data, item: data, isLoading, isError: error, mutate };
};

export function useVesselStampBlobUrl(
  vesselId: number | null | undefined,
  stampStorageKey: string | null | undefined,
) {
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const blobUrlRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!vesselId || !stampStorageKey) {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setBlobUrl(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get(`/vessels/${vesselId}/stamp`, {
        responseType: 'blob',
        headers: { Accept: 'image/*,application/octet-stream' },
      })
      .then((res) => {
        if (cancelled) return;
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        const blob = res.data as Blob;
        if (res.status === 204 || !blob || blob.size === 0) {
          setBlobUrl(null);
          return;
        }
        const type = (blob.type || '').toLowerCase();
        if (type.includes('json') || type.includes('text/')) {
          setBlobUrl(null);
          return;
        }
        const imageBlob =
          type.startsWith('image/') || type === 'application/octet-stream' || !type
            ? new Blob([blob], { type: type.startsWith('image/') ? type : 'image/png' })
            : blob;
        const url = URL.createObjectURL(imageBlob);
        blobUrlRef.current = url;
        setBlobUrl(url);
      })
      .catch(() => {
        if (!cancelled) setBlobUrl(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [vesselId, stampStorageKey]);

  return { stampBlobUrl: blobUrl, loading };
}

export async function uploadVesselStamp(id: number, file: File) {
  const formData = new FormData();
  formData.append('file', file, file.name || 'stamp.png');
  const res = await api.post(`/vessels/${id}/stamp`, formData);
  await globalMutate(`/vessels/${id}`);
  await globalMutate((key) => typeof key === 'string' && key.startsWith('/vessels'));
  return res.data as Vessel;
}

export async function deleteVesselStamp(id: number) {
  const res = await api.delete(`/vessels/${id}/stamp`);
  await globalMutate(`/vessels/${id}`);
  await globalMutate((key) => typeof key === 'string' && key.startsWith('/vessels'));
  return res.data as Vessel;
}
